<script lang="ts">
  import { t } from '$core/i18n';
  import {
    formatProblemDuration,
    type SessionStore
  } from '$features/session';
  import type { PreferencesStore } from '$core/preferences';
  import { Button } from '$ui/primitives/Button';
  import { Icon } from '$ui/primitives/Icon';
  import { Kbd } from '$ui/primitives/Kbd';
  import { Checkbox } from '$ui/primitives/Checkbox';

  let { store, prefs }: { store: SessionStore; prefs: PreferencesStore } = $props();

  let liveMs = $state(0);

  $effect(() => {
    if (store.problem.state !== 'running') return;
    const id = window.setInterval(() => {
      const started = store.problem.startedAt;
      liveMs = started === null ? 0 : Date.now() - started;
    }, 100);
    return () => window.clearInterval(id);
  });

  let displayedMs = $derived(
    store.problem.state === 'running' ? liveMs : store.problem.elapsedMs
  );

  let displayText = $derived(
    store.problem.state === 'running' && prefs.prefs.blindMode
      ? t('problemTimer.timePlaceholder')
      : formatProblemDuration(displayedMs)
  );

  let stateHint = $derived(
    store.problem.state === 'idle'
      ? t('modeB.hintIdle')
      : store.problem.state === 'running'
        ? t('modeB.hintRunning')
        : t('modeB.hintEvaluating')
  );

  let counterText = $derived(
    t('modeB.itemCounter', {
      current: store.reviewIndex + 1,
      total: store.reviewBatch.length
    })
  );
</script>

<div class="review-item">
  <header class="head">
    <span class="counter">{counterText}</span>
  </header>

  {#if store.reviewBatch.length > 1}
    <div
      class="progress"
      role="progressbar"
      aria-valuemin="1"
      aria-valuemax={store.reviewBatch.length}
      aria-valuenow={store.reviewIndex + 1}
      aria-label={counterText}
    >
      <div
        class="progress-fill"
        style="width: {((store.reviewIndex + 1) / store.reviewBatch.length) * 100}%"
      ></div>
    </div>
  {/if}

  {#if store.currentRecall}
    <div class="context">
      <span class="label">{t('modeB.contextLabel')}</span>
      <span class="value">
        {t('modeB.problemLabel')} {store.currentRecall.originalProblemNumber}
      </span>
    </div>
  {/if}

  <div
    class="clock"
    role="timer"
    aria-live="off"
    aria-describedby="modeb-state-hint"
  >{displayText}</div>

  <div class="hint-row">
    <span id="modeb-state-hint" class="hint" role="status" aria-live="polite">
      {stateHint}
    </span>
    <Button
      variant={prefs.prefs.blindMode ? 'filled' : 'outline'}
      size="sm"
      onclick={() => prefs.toggleBlindMode()}
      ariaLabel={prefs.prefs.blindMode
        ? t('problemTimer.blindModeOn')
        : t('problemTimer.blindModeOff')}
    >
      <Icon name={prefs.prefs.blindMode ? 'eye-off' : 'eye'} size={14} />
      {prefs.prefs.blindMode
        ? t('problemTimer.blindModeOn')
        : t('problemTimer.blindModeOff')}
    </Button>
  </div>

  {#if store.problem.state === 'evaluating'}
    <div class="rating">
      <div class="buttons-row" role="radiogroup" aria-label={t('modeB.ariaLabel')}>
        <div class="result-cell">
          <Button
            variant={store.currentRecallOutcome === 'good' ? 'filled' : 'outline'}
            size="lg"
            onclick={() => store.rateCurrentRecallOutcome('good')}
          >
            {t('modeB.correct')}
          </Button>
          <Kbd>1</Kbd>
        </div>
        <div class="result-cell">
          <Button
            variant={store.currentRecallOutcome === 'again' ? 'danger' : 'outline'}
            size="lg"
            onclick={() => store.rateCurrentRecallOutcome('again')}
          >
            {t('modeB.incorrect')}
          </Button>
          <Kbd>2</Kbd>
        </div>
      </div>

      <div class="consulted-wrap" title={t('modeB.consultedHint')}>
        <Checkbox
          bind:checked={store.reviewConsultedSolution}
          label={t('modeB.consulted')}
        />
      </div>

      <div class="actions">
        <Button
          variant="filled"
          size="lg"
          disabled={store.currentRecallOutcome === null}
          onclick={() => void store.commitCurrentRecall()}
        >
          {store.reviewIndex + 1 >= store.reviewBatch.length
            ? t('modeB.finishGroup')
            : t('modeB.next')}
        </Button>
      </div>
    </div>
  {/if}

  <div class="footer">
    <Button variant="outline" size="sm" onclick={() => void store.exitToGroups()}>
      ← {t('modeB.backToGroups')}
    </Button>
  </div>
</div>

<style>
  .review-item {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    width: 100%;
  }

  .head {
    display: flex;
    justify-content: flex-end;
    align-items: baseline;
  }

  .counter {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-size: var(--text-caption);
    color: var(--color-text-muted);
  }

  .context {
    display: flex;
    gap: var(--space-3);
    font-size: var(--text-sm);
    padding: var(--space-2) var(--space-3);
    background: var(--color-surface-sunken);
    border-radius: var(--radius-md);
  }

  .context .label {
    color: var(--color-text-muted);
  }

  .context .value {
    color: var(--color-text-primary);
    font-weight: var(--weight-medium);
  }

  .clock {
    font-family: var(--font-mono);
    font-size: clamp(2.5rem, 6vw, 3.5rem);
    font-weight: var(--weight-semibold);
    letter-spacing: 0.02em;
    color: var(--color-text-primary);
    text-align: center;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    min-height: 3rem;
    padding: var(--space-2) 0;
  }

  .hint-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .rating {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .buttons-row {
    display: flex;
    gap: var(--space-2);
  }

  .result-cell {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2);
  }

  .result-cell :global(.btn) {
    width: 100%;
  }

  .consulted-wrap {
    display: inline-flex;
    align-items: center;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
  }

  .footer {
    display: flex;
    justify-content: flex-start;
    padding-top: var(--space-2);
    border-top: var(--border-width) solid var(--color-border);
  }

  .progress {
    width: 100%;
    height: 4px;
    background: var(--color-surface-sunken);
    border-radius: var(--radius-full);
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--color-accent);
    border-radius: var(--radius-full);
    transition: width var(--dur-base) var(--ease-out);
  }
</style>